import { Box, Grid, IconButton } from '@mui/material';
import { useIsOverflow } from 'components/hooks/useCheckIfGridHasOverflow';
import React, { useCallback } from 'react';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { setSelectedServiceAgreement } from 'reducers/serviceAgreementsReducer';
import { selectBrUid, selectAccountNumber } from 'selectors/accountSelector';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { AppDispatch, RootState } from 'store';
import { fetchAllServiceAgreementsForAccount } from 'thunks/accountServiceAgreementsThunk';

type StickyHeaderWrapperProps = {
  topComponents?: React.ReactNode;
  centerComponents?: React.ReactNode;
  side?: 'left' | 'right' | string;
  hasStickyBottomBar?: boolean;
  hasOpenTask?: boolean;
  hasMultipleTasksOpen?: boolean;
};

const StickyHeaderWrapper = ({ topComponents, centerComponents, hasStickyBottomBar }: StickyHeaderWrapperProps) => {
  const { loader } = useSelector((state: RootState) => state.appConfig);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const brUid = useSelector(selectBrUid);
  const partnerUid = useSelector(selectPartnerId);
  const accountNumber = useSelector(selectAccountNumber);
  const location = useLocation();
  const { setRef: productDetailsRef, isOverflow: isOverflowing } = useIsOverflow();

  const handleBack = useCallback(() => {
    dispatch(setSelectedServiceAgreement(null));

    if (partnerUid) {
      dispatch(fetchAllServiceAgreementsForAccount(partnerUid, undefined, undefined, false));
      return navigate(`/partner/${partnerUid}`);
    } else {
      dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid, accountNumber, false));
    }

    return navigate(`/${brUid ? 'accounts' : 'partner'}/${brUid ?? partnerUid}`);
  }, [accountNumber, brUid, dispatch, navigate, partnerUid]);

  return (
    <Grid
      data-testid="sticky-header-wrapper"
      sx={{
        position: 'fixed',
        width: `calc(50% - 0.4em)`,
        height: hasStickyBottomBar ? 'calc(100% - 88px)' : 'calc(100% - 3px)',
        paddingLeft: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {
        <div
          data-testid="stick-header-wrapper-top-components"
          style={{
            paddingRight: `${isOverflowing ? '1.1em' : '0.6em'}`,
            marginBottom: -28,
          }}
        >
          {topComponents}
        </div>
      }
      {!loader.loading &&
        !location.pathname.includes('configuration') &&
        (location.pathname.includes('addProductOrService') || location.pathname.includes('serviceAgreement')) &&
        !location.pathname.includes('serviceRole') &&
        !location.pathname.includes('modification') && (
          <span
            style={{
              position: 'fixed',
              right: `${isOverflowing ? '2.4em' : '1.8em'}`,
              top: '1.5em',
            }}
          >
            <IconButton data-testid="sticky-header-wrapper-icon-button" onClick={handleBack}>
              <CloseIcon />
            </IconButton>
          </span>
        )}
      <Box
        ref={productDetailsRef}
        className="scrollbarThinContainer"
        sx={{
          mt: 1,
          flex: '1 1 0',
          minHeight: 0,
        }}
      >
        {centerComponents}
      </Box>
    </Grid>
  );
};

export default React.memo(StickyHeaderWrapper);
